import { useState } from "react";
import { X, Send, Loader2, AlertTriangle } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

/**
 * Non-admin delete flow: instead of deleting directly, raise a deletion request that
 * lands in the Admin/Founder Requests (Approvals) queue for a decision.
 */
export function RequestDeletionModal({ open, onOpenChange, entityType, entityId, entityLabel, onSubmitted }) {
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);

  if (!open) return null;

  async function submit() {
    if (!reason.trim()) { toast.error("Add a reason for the deletion request"); return; }
    setSubmitting(true);
    try {
      await api.post("/approvals", {
        entity_type: entityType,
        entity_id: entityId,
        request_kind: "deletion",
        reason: reason.trim(),
      });
      toast.success("Deletion request sent · an Admin will review it in Requests");
      setReason("");
      onOpenChange(false);
      onSubmitted?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send request");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-[100] bg-[#12324A]/45 backdrop-blur-sm flex items-start justify-center pt-20 px-4"
      onClick={() => onOpenChange(false)}
      data-testid="request-deletion-modal"
    >
      <div
        className="w-full max-w-md bg-[#F3FAFD] border border-[#BBD8E7] rounded-[26px] shadow-[0_24px_70px_rgba(7,83,154,0.22)] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-5 border-b border-[#C9DCE6] bg-[#E7F5FB] flex items-center gap-3">
          <div className="w-9 h-9 rounded-[12px] bg-white border border-[#EBC6C0] flex items-center justify-center text-[var(--danger)]">
            <AlertTriangle size={15} />
          </div>
          <div className="font-display text-[18px] text-[#12324A] flex-1">Request deletion?</div>
          <button onClick={() => onOpenChange(false)} data-testid="request-deletion-close" className="w-9 h-9 rounded-full bg-white border border-[#C9DCE6] flex items-center justify-center text-[#607D8B] hover:text-[#07539A] hover:border-[#07539A] transition-colors">
            <X size={16} />
          </button>
        </div>

        <div className="p-5 space-y-3">
          <p className="text-[12px] text-[#526D7A]">
            You don't have permission to delete {entityLabel ? <strong className="text-[#243B53]">{entityLabel}</strong> : "this record"} directly.
            This sends a deletion request to an Admin for approval — it shows up in their Requests queue.
          </p>
          <div className="rounded-[16px] bg-white border border-[#C9DCE6] p-3 shadow-[0_4px_14px_rgba(7,83,154,0.04)]">
            <label className="text-[10px] uppercase tracking-widest text-[#526D7A] font-semibold">Reason *</label>
            <textarea
              rows={3}
              autoFocus
              placeholder="Why does this need to be deleted?"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              data-testid="request-deletion-reason"
              className="mt-1.5 w-full bg-[#F8FCFE] border border-[#D7E8F0] rounded-[10px] px-3 py-2.5 text-[13px] text-[#243B53] resize-none outline-none focus:border-[#07539A] focus:bg-white transition-colors"
            />
          </div>
        </div>

        <div className="px-5 py-4 border-t border-[#C9DCE6] bg-[#E7F5FB] flex justify-end gap-3">
          <button
            onClick={() => onOpenChange(false)}
            className="h-10 px-4 rounded-[12px] text-[12px] border border-[#BBD3DF] bg-white text-[#607D8B] hover:text-[#07539A] hover:border-[#07539A] transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={submitting}
            data-testid="request-deletion-submit"
            className="h-10 px-5 rounded-[12px] text-[12px] font-medium bg-[var(--danger)] hover:opacity-90 !text-white disabled:opacity-50 flex items-center gap-2 shadow-[0_7px_18px_rgba(180,35,24,0.16)] transition-colors"
          >
            {submitting ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />}
            Send request
          </button>
        </div>
      </div>
    </div>
  );
}
